/**
 * bomTree.js
 * Pure, framework-free helpers for the BOM tree view (HU-08), job-per-component chips (HU-09) and
 * the parent "fully assigned" aggregation (HU-11). Extracted so the hierarchy building, the
 * job-assignment derivation and the recursive aggregation are all unit-testable without mounting
 * any React component (same pattern as `jobTaskIdentity.js`/`DocumentAppendOrderCalculator`).
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node Node object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource} WorkflowResource WorkflowResource object
 */

/**
 * @typedef {Object} JobAssignment
 * @property {string} jobNodeId The id of the JOB node the assignment belongs to
 * @property {string} jobCode The job's own code (HU-01/RN01.6)
 * @property {string} jobName The job's own free name (HU-01/RN01.2, may be empty)
 * @property {number} quantity Quantity assigned to this job (RN09.1)
 * @property {string} containerNodeId The node id to highlight on the current canvas: the job's own
 * id when it is a direct top-level node (S31), or the id of the top-level WORKFLOW node that
 * contains it when the job is nested inside a non-visible subworkflow (S32)
 * @property {boolean} isDirectlyVisible Whether the job node itself is rendered on the current
 * canvas (top-level) or nested inside a subworkflow not currently open (S31/S32/D5)
 */

/**
 * S27 (RN08.3): normalizes a dotted BOM "position" (e.g. "1.2.0") by dropping trailing zero
 * segments, so "1.2.0" and "1.2" refer to the same hierarchy level.
 * @param {string} position
 * @returns {string}
 */
const normalizeBomPosition = (position) => {
  if (!position) {
    return position || ''
  }

  const parts = String(position).split('.')
  while (parts.length > 1 && Number(parts.at(-1)) === 0) {
    parts.pop()
  }

  return parts.join('.')
}

/**
 * S27 (RN08.3): builds the parents-children hierarchy of a flat BOM component list from their
 * dotted "position" field, in a read-only, framework-free shape (each item gains a `children`
 * array). Same algorithm as `resourcetypes/WorkflowResourcesTypes.jsx`'s internal tree building,
 * extracted here as a pure function for the new read-only BOM tree view.
 * @param {WorkflowResource[]} items
 * @returns {WorkflowResource[]} Top-level items, each with a `children` array
 */
const buildBomPositionTree = (items) => {
  if (!Array.isArray(items)) {
    return []
  }

  const tree = []
  const parentMap = {}
  const nodes = items.map((item) => ({ ...item, children: [], normalizedPosition: normalizeBomPosition(item.position) }))

  nodes.forEach((node) => {
    if (!parentMap[node.normalizedPosition]) {
      parentMap[node.normalizedPosition] = node
    }
  })

  nodes.forEach((node) => {
    const position = String(node.normalizedPosition)
    const lastDotIndex = position.lastIndexOf('.')

    if (lastDotIndex === -1) {
      tree.push(node)
      return
    }

    // The prefix must be normalized too: a parent stored as "390.00" is indexed as "390", so the
    // raw prefix "390.00" of a child "390.00.10" would never find it.
    const parentPosition = normalizeBomPosition(position.substring(0, lastDotIndex))
    if (parentMap[parentPosition]) {
      parentMap[parentPosition].children.push(node)
    } else {
      tree.push(node)
    }
  })

  return tree
}

/**
 * S65 (historia 152, RN-02.2): the position of a component's IMMEDIATE parent, normalized at both
 * ends — a parent stored as "1.2.0" owns the child "1.2.1", and a first-level component ("2") has
 * no parent position at all. Same prefix rule `buildBomPositionTree` already applies, extracted so
 * the hierarchy and the "Parent" column of the node's resources window cannot drift apart.
 * @param {string} position
 * @returns {string} The parent's normalized position, or '' for a first-level component
 */
const parentBomPosition = (position) => {
  const normalized = String(normalizeBomPosition(position) || '')
  const lastDotIndex = normalized.lastIndexOf('.')

  return lastDotIndex === -1 ? '' : normalizeBomPosition(normalized.substring(0, lastDotIndex))
}

/**
 * S64/S65 (historia 152, RN-02.1/RN-02.2): indexes, per normalized position, the CODE of the
 * component's immediate parent assembly, derived from the workflow's own flat BOM list. A
 * component whose parent position holds no component (a first-level one, or a BOM with a gap in
 * its positions) is indexed as '' — the parent is declared empty, never invented.
 * @param {WorkflowResource[]} components The workflow's BOM, flat, as `workflowResource/*` returns it
 * @returns {Object<string, string>} normalized position -> immediate parent's code ('' when none)
 */
const buildParentCodeByPosition = (components) => {
  const codeByPosition = {}
  ;(components || []).forEach((component) => {
    const position = normalizeBomPosition(component?.position)
    if (!position || codeByPosition[position]) {
      return
    }
    codeByPosition[position] = component?.resource?.code || ''
  })

  const parentCodeByPosition = {}
  Object.keys(codeByPosition).forEach((position) => {
    parentCodeByPosition[position] = codeByPosition[parentBomPosition(position)] || ''
  })

  return parentCodeByPosition
}

/**
 * S64/S65 (historia 152, RN-02.1/RN-02.2): the parent assembly's code for a component at
 * `position`, read from the index above. Unknown component or first level resolve to '' (the
 * repo's empty default), so the column declares "no parent" instead of inventing one.
 * @param {string} position The component's `position`, raw (normalized here)
 * @param {Object<string, string>} parentCodeByPosition Index built by `buildParentCodeByPosition`
 * @returns {string}
 */
const resolveParentComponentCode = (position, parentCodeByPosition) => {
  return parentCodeByPosition?.[normalizeBomPosition(position)] || ''
}

/**
 * S28/S31/S32 (RN09.1/RN09.4/D5): recursively derives, for every `workflowResourceUUID`, the list
 * of jobs assigned to it, from `node.jobReference.resources[].resourceQuantityBOM
 * .workflowResourceUUID` — walking not only the top-level nodes but also every nested
 * `node.subWorkflow.nodes` level, since a component can be consumed by a job several levels deep.
 * Every assignment carries `containerNodeId`: the job's own id when it is a top-level node (S31),
 * or the id of the top-level WORKFLOW node that contains it otherwise (S32) — the only node ids
 * that exist on the currently rendered graph canvas.
 * @param {Node[]} nodes Top-level nodes of the currently loaded workflow (workflowData.workflow.nodes)
 * @returns {Object<string, JobAssignment[]>} Map of workflowResourceUUID -> JobAssignment[]
 */
const collectJobAssignments = (nodes) => {
  const assignmentsByUUID = {}

  const addAssignment = (workflowResourceUUID, assignment) => {
    if (!assignmentsByUUID[workflowResourceUUID]) {
      assignmentsByUUID[workflowResourceUUID] = []
    }
    assignmentsByUUID[workflowResourceUUID].push(assignment)
  }

  const visitJobNode = (node, containerNodeId, isDirectlyVisible) => {
    const resources = node.jobReference?.resources || []
    resources.forEach((resource) => {
      const workflowResourceUUID = resource.resourceQuantityBOM?.workflowResourceUUID
      if (!workflowResourceUUID) {
        return
      }
      addAssignment(workflowResourceUUID, {
        jobNodeId: node.id,
        jobCode: node.code || '',
        jobName: node.name || '',
        quantity: resource.quantityFirstUnit ?? 0,
        containerNodeId,
        isDirectlyVisible
      })
    })
  }

  const visitNodes = (nodeList, containerNodeId, isDirectlyVisible) => {
    (nodeList || []).forEach((node) => {
      if (node.nodeType === 'JOB') {
        visitJobNode(node, isDirectlyVisible ? node.id : containerNodeId, isDirectlyVisible)
        return
      }
      if (node.nodeType === 'WORKFLOW') {
        visitNodes(node.subWorkflow?.nodes, isDirectlyVisible ? node.id : containerNodeId, false)
      }
    })
  }

  visitNodes(nodes, '', true)

  return assignmentsByUUID
}

/**
 * S30 (RN09.3): a component with no assignment resolves an empty array, never null/undefined —
 * the caller renders the explicit "Not assigned" indication for that case.
 * @param {string} workflowResourceUUID
 * @param {Object<string, JobAssignment[]>} assignmentsByUUID
 * @returns {JobAssignment[]}
 */
const getJobAssignmentsFor = (workflowResourceUUID, assignmentsByUUID) => {
  return assignmentsByUUID?.[workflowResourceUUID] || []
}

/**
 * S31/S32 (RN09.4/D5): resolves which node id must be highlighted on the graph canvas when a job
 * chip is clicked — the job's own node when visible on the current canvas (S31), or its container
 * subworkflow node when the job is nested inside a subworkflow not currently open (S32). Never
 * triggers navigation, pan, zoom or selection — the caller only flips a highlight flag on node data.
 * @param {JobAssignment} assignment
 * @returns {string}
 */
const resolveHighlightTargetNodeId = (assignment) => {
  return assignment.isDirectlyVisible ? assignment.jobNodeId : assignment.containerNodeId
}

/**
 * Decorates every level of a BOM tree (as built by `buildBomPositionTree`) with its own
 * `jobAssignments`, without mutating the input — used by the tree view to render per-item chips
 * (S28/S29/S30) and to feed `computeAssignmentCompletionStatus` (S35-S37).
 * @param {WorkflowResource[]} items
 * @param {Object<string, JobAssignment[]>} assignmentsByUUID
 * @returns {WorkflowResource[]}
 */
const attachJobAssignments = (items, assignmentsByUUID) => {
  return (items || []).map((item) => ({
    ...item,
    jobAssignments: getJobAssignmentsFor(item.uuid, assignmentsByUUID),
    children: attachJobAssignments(item.children, assignmentsByUUID)
  }))
}

const ASSIGNMENT_STATUS = {
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  COMPLETE: 'COMPLETE',
  INCOMPLETE: 'INCOMPLETE'
}

/**
 * S35/S36/S37 (RN11.1-RN11.4): recursively aggregates whether every descendant of a BOM component
 * is assigned to at least one job. A component without children does not apply to this indicator
 * (S37, NOT_APPLICABLE) — the aggregation only evaluates parents. A parent is COMPLETE (S35) iff
 * every one of its children is either itself assigned (leaf) or itself COMPLETE (non-leaf); any
 * unassigned child makes it INCOMPLETE (S36). ATA/QTA fields are never read or changed here —
 * only `children`/`jobAssignments` feed the aggregation, matching RN11.2 (no manual parent
 * assignment, no ATA/QTA change).
 * @param {WorkflowResource & {children: WorkflowResource[], jobAssignments: JobAssignment[]}} item
 * @returns {'NOT_APPLICABLE'|'COMPLETE'|'INCOMPLETE'}
 */
const computeAssignmentCompletionStatus = (item) => {
  // HU-13/S44: a component removed by the last BOM import is shown but no longer belongs to the
  // BOM, so it never takes part in its parent's aggregation.
  const children = Array.isArray(item.children) ? item.children.filter((child) => !child.deleted) : []
  if (children.length === 0) {
    return ASSIGNMENT_STATUS.NOT_APPLICABLE
  }

  const isLeafAssigned = (child) => Array.isArray(child.jobAssignments) && child.jobAssignments.length > 0

  const allChildrenSatisfied = children.every((child) => {
    const childStatus = computeAssignmentCompletionStatus(child)
    return childStatus === ASSIGNMENT_STATUS.NOT_APPLICABLE ? isLeafAssigned(child) : childStatus === ASSIGNMENT_STATUS.COMPLETE
  })

  return allChildrenSatisfied ? ASSIGNMENT_STATUS.COMPLETE : ASSIGNMENT_STATUS.INCOMPLETE
}

/**
 * S31/S32 (RN09.4/RN09.5/D5): the blue the job highlight is drawn in — on the graph node (border,
 * `TechnologyNode`) and on the BOM tree row the highlight was requested from, so both ends of the
 * same action are recognizable as one. Lives here, next to `applyJobHighlight`, so the two views
 * cannot drift apart. Hardcoded like the node's other highlight colors, so the light theme (orange
 * primary) never repaints it.
 */
const JOB_HIGHLIGHT_COLOR = '#4FC3F7'

/**
 * S31/S32 (RN09.4/RN09.5/D5): applies the job highlight to the already-rendered reactflow `nodes`
 * array by directly patching `data.jobHighlighted` (true only for `targetNodeId`, false for every
 * other node) — never rebuilding node data from the workflow model, which would re-trigger
 * `fitView` (pan/zoom) on `SmartReactflow`, and never touching node `selected`/`selectable`
 * state, so no node gets (re)selected either.
 * S69 (historia 152, RN-02.6, §6.2 decisión de diseño nº 4): the target is now a `Set` of node
 * ids — selecting a BOM assembly highlights every operation that consumes it. A `Set` of one (or a
 * plain id, still accepted) is literally the behaviour of #28 S31/S32, which is why this is an
 * extension of cardinality and not a second highlight mechanism.
 * @param {import('reactflow').Node[]} nodes Currently rendered reactflow nodes
 * @param {Set<string>|string} targetNodeIds The node id(s) to highlight (resolved via
 * `resolveHighlightTargetNodeId` / `assemblyHighlight.js#collectAssemblyNodeIds`)
 * @returns {import('reactflow').Node[]}
 */
const applyJobHighlight = (nodes, targetNodeIds) => {
  const highlighted = asNodeIdSet(targetNodeIds)

  return (nodes || []).map((node) => ({
    ...node,
    data: { ...node.data, jobHighlighted: highlighted.has(node.id) }
  }))
}

/**
 * S69/S70 (historia 152, RN-02.6 point 2): the same technique applied to the already-rendered
 * reactflow `edges`, for the connections BETWEEN the highlighted operations (which edges those are
 * is `assemblyHighlight.js#edgesBetween`'s call, never this one's). Patching `data` in place — no
 * rebuild — for the same reason as the nodes above: a rebuild re-triggers `fitView` (pan/zoom),
 * which `RN-02.7` forbids.
 * @param {import('reactflow').Edge[]} edges Currently rendered reactflow edges
 * @param {Set<string>|string} highlightedEdgeIds
 * @returns {import('reactflow').Edge[]}
 */
const applyEdgeHighlight = (edges, highlightedEdgeIds) => {
  const highlighted = asNodeIdSet(highlightedEdgeIds)

  return (edges || []).map((edge) => ({
    ...edge,
    data: { ...edge.data, jobHighlighted: highlighted.has(edge.id) }
  }))
}

/**
 * One id, a `Set` of them or nothing at all, always as a `Set` — so the single-target call of #28
 * and the assembly call of #152 share one implementation.
 * @param {Set<string>|string[]|string} ids
 * @returns {Set<string>}
 */
const asNodeIdSet = (ids) => {
  if (ids instanceof Set) {
    return ids
  }
  if (Array.isArray(ids)) {
    return new Set(ids)
  }
  return new Set(ids ? [ids] : [])
}

/**
 * Story 118 (RN62): marks ONE node as selected on the already-rendered reactflow `nodes` array, patching
 * both `selected` (reactflow's own flag, kept in sync by `SmartReactflow#onSelectionChange`) and
 * `data.selected` (the flag `TechnologyNode` paints the selection border from) — never rebuilding node
 * data from the workflow model, which would re-trigger `fitView` and jump the pan/zoom, exactly as
 * `applyJobHighlight` above avoids.
 *
 * It lives here, next to `applyJobHighlight`, because it is the SAME technique and its one difference is
 * the one that neighbour declares: this function DOES touch `selected`, deliberately. That is what makes
 * a click on a tree row select the node for the whole screen (the shared selection of HU-08). Keeping the
 * two apart in different files would guarantee they drift.
 * @param {import('reactflow').Node[]} nodes Currently rendered reactflow nodes
 * @param {string} targetNodeId The node to select — the FIRST-LEVEL node id (see `resolveTopLevelNodeId`)
 * @returns {import('reactflow').Node[]}
 */
const applyNodeSelection = (nodes, targetNodeId) => {
  return (nodes || []).map((node) => {
    const selected = node.id === targetNodeId
    return { ...node, selected, data: { ...node.data, selected } }
  })
}

export {
  normalizeBomPosition,
  parentBomPosition,
  buildParentCodeByPosition,
  resolveParentComponentCode,
  buildBomPositionTree,
  collectJobAssignments,
  getJobAssignmentsFor,
  resolveHighlightTargetNodeId,
  attachJobAssignments,
  applyJobHighlight,
  applyEdgeHighlight,
  applyNodeSelection,
  JOB_HIGHLIGHT_COLOR,
  ASSIGNMENT_STATUS,
  computeAssignmentCompletionStatus
}
